// The runtime half of element styles (element-styles.ts): how a linked element takes its style,
// with no schema library, so a creator site's script stays small.
import type { PageNode, PageNodeStyle } from "./pages";

/**
 * A creator's own named style, shared by every element linked to it (as Carrd's element styles
 * and Figma's shared styles work): editing one linked element edits the style, so all of them
 * change. A style belongs to one kind of element.
 */
export type ElementStyle = {
  name: string;
  // The kind of element it styles (PageNode["kind"]).
  kind: string;
  style: Omit<PageNodeStyle, "elementStyle">;
};
export type ElementStyles = Record<string, ElementStyle>;

/** The element's style with its shared style beneath its own settings. */
export function withElementStyle<S extends { elementStyle?: string }>(
  style: S | undefined,
  styles: ElementStyles | undefined,
): S | undefined {
  const shared = style?.elementStyle ? styles?.[style.elementStyle] : undefined;
  return shared ? ({ ...shared.style, ...style } as S) : style;
}

/** The element as it draws: its shared style applied. */
export function elementStyled<N extends PageNode>(node: N, styles: ElementStyles | undefined): N {
  if (!node.style?.elementStyle || !styles) return node;
  return { ...node, style: withElementStyle(node.style, styles) };
}

export function newElementStyleId() {
  return `style-${crypto.randomUUID().replace(/-/g, "").slice(0, 10)}`;
}
